// Helpers for the WebAuthn (passkey) browser API.
//
// The backend exchanges WebAuthn payloads in the standard JSON serialization
// format (binary fields encoded as base64url strings), while the browser API
// works with ArrayBuffers. Native converters
// (PublicKeyCredential.parseRequestOptionsFromJSON / credential.toJSON) are
// not available in all supported browsers, so the conversion is done manually.

export interface PublicKeyCredentialDescriptorJSON {
  id: string
  type: PublicKeyCredentialType
  transports?: AuthenticatorTransport[]
}

export interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string
  timeout?: number
  rpId?: string
  allowCredentials?: PublicKeyCredentialDescriptorJSON[]
  userVerification?: UserVerificationRequirement
}

export interface PublicKeyCredentialCreationOptionsJSON {
  rp: PublicKeyCredentialRpEntity
  user: {
    id: string
    name: string
    displayName: string
  }
  challenge: string
  pubKeyCredParams: PublicKeyCredentialParameters[]
  timeout?: number
  excludeCredentials?: PublicKeyCredentialDescriptorJSON[]
  authenticatorSelection?: AuthenticatorSelectionCriteria
  attestation?: AttestationConveyancePreference
}

export interface AuthenticationResponseJSON {
  id: string
  rawId: string
  type: string
  authenticatorAttachment?: string
  clientExtensionResults: AuthenticationExtensionsClientOutputs
  response: {
    clientDataJSON: string
    authenticatorData: string
    signature: string
    userHandle?: string
  }
}

export interface RegistrationResponseJSON {
  id: string
  rawId: string
  type: string
  authenticatorAttachment?: string
  clientExtensionResults: AuthenticationExtensionsClientOutputs
  response: {
    clientDataJSON: string
    attestationObject: string
    transports?: string[]
  }
}

const base64UrlToBuffer = (value: string): ArrayBuffer => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
  const binary = window.atob(padded)
  const bytes = new Uint8Array(binary.length)

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }

  return bytes.buffer
}

const bufferToBase64Url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer)
  let binary = ''

  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }

  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export const isWebAuthnSupported = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.PublicKeyCredential === 'function' &&
  !!navigator.credentials

export const isConditionalMediationAvailable = async (): Promise<boolean> => {
  try {
    if (!isWebAuthnSupported()) {
      return false
    }

    if (typeof PublicKeyCredential.isConditionalMediationAvailable !== 'function') {
      return false
    }

    return await PublicKeyCredential.isConditionalMediationAvailable()
  } catch {
    return false
  }
}

export const parseRequestOptions = (
  options: PublicKeyCredentialRequestOptionsJSON
): PublicKeyCredentialRequestOptions => ({
  ...options,
  challenge: base64UrlToBuffer(options.challenge),
  allowCredentials: options.allowCredentials?.map((credential) => ({
    ...credential,
    id: base64UrlToBuffer(credential.id),
  })),
})

export const parseCreationOptions = (
  options: PublicKeyCredentialCreationOptionsJSON
): PublicKeyCredentialCreationOptions => ({
  ...options,
  challenge: base64UrlToBuffer(options.challenge),
  user: {
    ...options.user,
    id: base64UrlToBuffer(options.user.id),
  },
  excludeCredentials: options.excludeCredentials?.map((credential) => ({
    ...credential,
    id: base64UrlToBuffer(credential.id),
  })),
})

export const authenticationResponseToJSON = (
  credential: PublicKeyCredential
): AuthenticationResponseJSON => {
  const response = credential.response as AuthenticatorAssertionResponse

  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      authenticatorData: bufferToBase64Url(response.authenticatorData),
      signature: bufferToBase64Url(response.signature),
      userHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : undefined,
    },
  }
}

export const registrationResponseToJSON = (
  credential: PublicKeyCredential
): RegistrationResponseJSON => {
  const response = credential.response as AuthenticatorAttestationResponse

  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      attestationObject: bufferToBase64Url(response.attestationObject),
      transports:
        typeof response.getTransports === 'function' ? response.getTransports() : undefined,
    },
  }
}
